import * as Sentry from '@sentry/core';

// @ts-strict-ignore
import 'common/types/globals/window';

import {isBrowserEnv} from './util/env';

type LogLevelString = `error` | `warn` | `info`;

/** wrap a string in Error to generate a trace for logging. */
function maybeWrapAsError(arg: string | Error) {
  if (!(arg instanceof Error)) {
    return new Error(arg);
  }
  return arg;
}

/**
 * Reports some kind of message to console and Sentry.
 *
 * Raw strings will be wrapped in an Error to preserve
 * a stack trace.
 *
 * @param logLevel
 * @param {Error|string} error
 * @param {object|string} info extra parameters to log, or a nice message
 * @param {any[]} rest any additional params for sentry
 */
function reportToConsoleAndSentry(logLevel: LogLevelString, message: string | Error, info?: any, ...rest: any[]) {
  const maybeError = maybeWrapAsError(message);
  // eslint-disable-next-line no-console
  console[logLevel](message, info, ...rest);

  if (isBrowserEnv() && window.sentry) {
    logLevel === `error`
      ? window.sentry.captureException(maybeError, {...info, ...rest})
      : window.sentry.captureMessage(maybeError.toString(), Sentry.severityLevelFromString(logLevel), {
          ...info,
          ...rest,
        });
  }
}

function createReporterFunc(logLevel) {
  return function (message: string | Error, info?: any, ...rest: any[]) {
    reportToConsoleAndSentry(logLevel, message, info, ...rest);
  };
}

/**
 * @example
 * reporter.info(`some message to report`);
 * reporter.error(new Error(`everything is on fire`));
 */
export const reporter = {
  error: createReporterFunc(`error`),
  warn: createReporterFunc(`warn`),
  info: createReporterFunc(`info`),
};
